/**
 * フラットな MenuResponse 配列から、サイドバー用のツリー構造に組み立てる
 */

import type { TFunction } from "i18next";
import type { MenuResponse } from "../../api/menus";
import { resolveFormula } from "./resolveFormula";
import { MENU_TYPE, type MenuItem, type SidebarMenus } from "./menuTypes";

/**
 * 権限・表示フラグで絞り込み
 */
function filterByPermission(
    items: MenuResponse[],
    permissionLevel: number,
): MenuResponse[] {
    return items.filter((m) => {
        if (m.is_display !== 1) return false;
        return permissionLevel >= m.min_role_view;
    });
}

/**
 * MenuResponse → MenuItem 変換（翻訳適用、children は空配列で初期化）
 */
function toMenuItem(m: MenuResponse, t: TFunction): MenuItem {
    return {
        id: m.id,
        code: m.code,
        type: m.type,
        parentId: m.parent_id,
        name: resolveFormula(m.name_formula, t, m.name),
        url: m.url,
        icon: m.icon,
        displayOrder: m.display_order,
        children: [],
    };
}

/**
 * displayOrder 昇順ソート
 */
function sortByDisplayOrder(items: MenuItem[]): MenuItem[] {
    return [...items].sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * 指定の親・子typeに該当する子メニューを取得
 */
function findChildren(
    parentId: number,
    allItems: MenuItem[],
    childType: string,
): MenuItem[] {
    const children = allItems.filter(
        (m) => m.parentId === parentId && m.type === childType,
    );
    return sortByDisplayOrder(children);
}

/**
 * フラット配列からサイドバー構造を構築する
 *
 * 1回のAPIコール（platform=web 全件取得）の結果を受け取り、
 * 上部/下部 × 親子 の形に整形する。
 */
export function buildSidebarMenus(
    dbMenus: MenuResponse[],
    t: TFunction,
    permissionLevel: number,
): SidebarMenus {
    const filtered = filterByPermission(dbMenus, permissionLevel);
    const items = filtered.map((m) => toMenuItem(m, t));

    // 上部メニュー: children = tab
    const topRoots = items.filter((m) => m.type === MENU_TYPE.SIDEBAR_TOP);
    const top = sortByDisplayOrder(topRoots).map((root) => ({
        ...root,
        children: findChildren(root.id, items, MENU_TYPE.TAB),
    }));

    // 下部メニュー: children = subitem
    const bottomRoots = items.filter((m) => m.type === MENU_TYPE.SIDEBAR_BOTTOM);
    const bottom = sortByDisplayOrder(bottomRoots).map((root) => ({
        ...root,
        children: findChildren(root.id, items, MENU_TYPE.SUBITEM),
    }));

    return { top, bottom };
}

/**
 * 指定の URL に一致するメニュー項目（およびその親チェーン）を探す
 *
 * 戻り値:
 *   - item: 一致したメニュー項目
 *   - parent: 親メニュー（タブの場合のみ、それ以外は null）
 *   見つからない場合は null
 */
export function findMenuByUrl(
    menus: SidebarMenus,
    url: string,
): { item: MenuItem; parent: MenuItem | null } | null {
    const sections: MenuItem[][] = [menus.top, menus.bottom];
    for (const section of sections) {
        for (const root of section) {
            if (root.url === url) return { item: root, parent: null };
            for (const child of root.children) {
                if (child.url === url) return { item: child, parent: root };
            }
        }
    }
    return null;
}

/**
 * 指定メニューIDの子メニュー（タブ等）を取得するヘルパー
 * 各ページでタブ一覧を取得する際に使用
 */
export function getChildrenById(
    menus: SidebarMenus,
    parentId: number,
): MenuItem[] {
    for (const root of menus.top) {
        if (root.id === parentId) return root.children;
    }
    for (const root of menus.bottom) {
        if (root.id === parentId) return root.children;
    }
    return [];
}
